import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Camera,
  Trash2,
  Save,
  AlertTriangle,
  Sun,
  Moon,
  Monitor,
  Loader2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import Avatar from '@/components/Avatar';

/**
 * ProfileEdit
 * ---------------------------------------------------------------------------
 * Comprehensive profile management:
 *   - Avatar (upload / remove)
 *   - Display name + bio
 *   - Theme preference (dark/light/system)
 *   - Email change (requires current password if password is set)
 *   - Password change (requires current password unless OAuth-only)
 *   - Delete account (requires email + password confirmation)
 */
export default function ProfileEdit() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const { preference: themePref, setPreference: setThemePref } = useTheme();
  const fileInputRef = useRef(null);

  // -------- Profile fields --------
  // NOTE: display_name was removed in favor of username (the unique public
  // handle). Existing display_name values are migrated to username at app
  // startup if a user has no username yet (see server.py migration).
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Client-side mirror of the backend username rule so we surface the error
  // inline before the PATCH round-trip. Empty string is valid (means "clear
  // the username" in our API contract).
  const trimmedUsername = username.trim();
  const usernameError = (() => {
    if (!trimmedUsername) return null;
    if (trimmedUsername.length < 3 || trimmedUsername.length > 30) {
      return 'Username must be 3\u201330 characters';
    }
    if (!/^[A-Za-z0-9](?:[A-Za-z0-9_-]{1,28}[A-Za-z0-9])?$/.test(trimmedUsername)) {
      return 'Letters, numbers, _ and - only \u2014 no spaces';
    }
    return null;
  })();

  // -------- Email / password --------
  const [newEmail, setNewEmail] = useState('');
  const [currentPasswordForEmail, setCurrentPasswordForEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // -------- Account deletion --------
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  // -------- Avatar upload --------
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setBio(user.bio || '');
    }
  }, [user]);

  const hasPassword = !!user?.password_hash || (user?.auth_providers || []).includes('email');

  // ---------------- Handlers ----------------
  const handleSaveProfile = async () => {
    if (usernameError) {
      toast.error(usernameError);
      return;
    }
    setSavingProfile(true);
    try {
      // Send username as the trimmed value (or empty string to clear it).
      // The backend treats `username: null` as "don't touch" \u2014 we always
      // send a string so that user-driven clears propagate.
      // baseURL already includes /api — don't double-prefix.
      await api.patch('/profile/me', {
        bio,
        username: trimmedUsername,
      });
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleThemeChange = async (pref) => {
    setThemePref(pref);
    try {
      await api.patch('/profile/me', { theme_preference: pref });
    } catch {
      // Non-fatal — local preference is already applied
    }
  };

  const handleAvatarPick = () => fileInputRef.current?.click();

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Use JPG, PNG, WEBP, or GIF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }
    setUploadingAvatar(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post('/profile/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshUser();
      toast.success('Avatar updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Avatar upload failed');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    setUploadingAvatar(true);
    try {
      await api.delete('/profile/avatar');
      await refreshUser();
      toast.success('Avatar removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleEmailChange = async () => {
    if (!newEmail.trim()) {
      toast.error('Enter a new email address');
      return;
    }
    if (hasPassword && !currentPasswordForEmail) {
      toast.error('Enter your current password');
      return;
    }
    setSavingEmail(true);
    try {
      await api.post('/profile/email', {
        new_email: newEmail.trim(),
        current_password: currentPasswordForEmail || undefined,
      });
      setNewEmail('');
      setCurrentPasswordForEmail('');
      await refreshUser();
      toast.success('Email updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (hasPassword && !currentPassword) {
      toast.error('Enter your current password');
      return;
    }
    setSavingPassword(true);
    try {
      await api.post('/profile/password', {
        current_password: currentPassword || undefined,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(hasPassword ? 'Password updated' : 'Password set');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteEmail.trim().toLowerCase() !== (user?.email || '').toLowerCase()) {
      toast.error("Email confirmation doesn't match");
      return;
    }
    if (hasPassword && !deletePassword) {
      toast.error('Enter your password to confirm deletion');
      return;
    }
    setDeleting(true);
    try {
      await api.delete('/account', {
        data: {
          confirm_email: deleteEmail.trim(),
          current_password: deletePassword || undefined,
        },
      });
      toast.success('Account deleted. Goodbye 👋');
      logout();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete account');
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-muted-foreground">
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-8" data-testid="profile-edit-page">
      {/* Back / Title */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/profile')}
          data-testid="profile-edit-back-btn"
          aria-label="Back to profile"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">Edit profile</h1>
          <p className="text-sm text-muted-foreground">Customize how you appear in GroovLens.</p>
        </div>
      </div>

      {/* ============ Avatar + display name + bio ============ */}
      <Card data-testid="profile-edit-basics-card">
        <CardHeader>
          <CardTitle>Identity</CardTitle>
          <CardDescription>Your name, photo, and bio.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-5">
            <Avatar user={user} size={96} dataTestId="profile-edit-avatar" />
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleAvatarFile}
                data-testid="profile-edit-avatar-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleAvatarPick}
                disabled={uploadingAvatar}
                data-testid="profile-edit-avatar-upload-btn"
              >
                {uploadingAvatar ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {user.avatar_path ? 'Change photo' : 'Upload photo'}
              </Button>
              {user.avatar_path && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAvatarRemove}
                  disabled={uploadingAvatar}
                  data-testid="profile-edit-avatar-remove-btn"
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Remove photo
                </Button>
              )}
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              maxLength={30}
              placeholder="yourhandle"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              aria-invalid={!!usernameError}
              aria-describedby="username-edit-hint"
              data-testid="profile-edit-username"
            />
            <p
              id="username-edit-hint"
              className={`text-xs ${usernameError ? 'text-[hsl(var(--destructive))]' : 'text-muted-foreground'}`}
              data-testid="profile-edit-username-hint"
            >
              {usernameError
                ? usernameError
                : 'Lets you sign in with your handle instead of your email. Letters, numbers, _ and - only \u2014 3\u201330 chars.'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={280}
              placeholder="A short bio. 280 characters max."
              rows={3}
              data-testid="profile-edit-bio"
            />
            <p className="text-xs text-muted-foreground text-right">{bio.length}/280</p>
          </div>

          <Button
            onClick={handleSaveProfile}
            disabled={savingProfile}
            data-testid="profile-edit-save-btn"
          >
            {savingProfile ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save changes
          </Button>
        </CardContent>
      </Card>

      {/* ============ Theme ============ */}
      <Card data-testid="profile-edit-theme-card">
        <CardHeader>
          <CardTitle>Theme</CardTitle>
          <CardDescription>Choose how GroovLens looks on your device.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'dark', label: 'Dark', icon: Moon },
              { value: 'light', label: 'Light', icon: Sun },
              { value: 'system', label: 'System', icon: Monitor },
            ].map(({ value, label, icon: Icon }) => (
              <Button
                key={value}
                variant={themePref === value ? 'default' : 'outline'}
                onClick={() => handleThemeChange(value)}
                data-testid={`profile-edit-theme-${value}`}
                className="flex flex-col h-auto py-3 gap-1.5"
              >
                <Icon className="w-4 h-4" />
                <span className="text-xs">{label}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ============ Email ============ */}
      <Card data-testid="profile-edit-email-card">
        <CardHeader>
          <CardTitle>Email</CardTitle>
          <CardDescription>Currently: {user.email}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new_email">New email</Label>
            <Input
              id="new_email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@groovlens.com"
              data-testid="profile-edit-new-email"
            />
          </div>
          {hasPassword && (
            <div className="space-y-2">
              <Label htmlFor="current_password_for_email">Current password</Label>
              <Input
                id="current_password_for_email"
                type="password"
                value={currentPasswordForEmail}
                onChange={(e) => setCurrentPasswordForEmail(e.target.value)}
                placeholder="••••••••"
                data-testid="profile-edit-current-password-for-email"
              />
            </div>
          )}
          <Button
            onClick={handleEmailChange}
            disabled={savingEmail || !newEmail.trim()}
            data-testid="profile-edit-change-email-btn"
          >
            {savingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Change email
          </Button>
        </CardContent>
      </Card>

      {/* ============ Password ============ */}
      <Card data-testid="profile-edit-password-card">
        <CardHeader>
          <CardTitle>{hasPassword ? 'Change password' : 'Set password'}</CardTitle>
          <CardDescription>
            {hasPassword
              ? 'Update the password used for email/password login.'
              : 'Set a password so you can sign in with email (currently OAuth only).'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasPassword && (
            <div className="space-y-2">
              <Label htmlFor="current_password">Current password</Label>
              <Input
                id="current_password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="profile-edit-current-password"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="new_password">New password</Label>
            <Input
              id="new_password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              data-testid="profile-edit-new-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm new password</Label>
            <Input
              id="confirm_password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat the new password"
              data-testid="profile-edit-confirm-password"
            />
          </div>
          <Button
            onClick={handlePasswordChange}
            disabled={savingPassword || !newPassword || !confirmPassword}
            data-testid="profile-edit-change-password-btn"
          >
            {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {hasPassword ? 'Update password' : 'Set password'}
          </Button>
        </CardContent>
      </Card>

      {/* ============ Danger Zone ============ */}
      <Card className="border-destructive/40" data-testid="profile-edit-danger-card">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Danger zone
          </CardTitle>
          <CardDescription>Once you delete your account, this can&rsquo;t be undone.</CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="profile-edit-delete-account-trigger">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete my account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-account-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your GroovLens account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes your crates, scans, subscription record, and all
                  uploaded files. <strong>This action cannot be undone.</strong>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5">
                  <Label htmlFor="delete_email">
                    Type <strong>{user.email}</strong> to confirm
                  </Label>
                  <Input
                    id="delete_email"
                    type="email"
                    value={deleteEmail}
                    onChange={(e) => setDeleteEmail(e.target.value)}
                    data-testid="delete-account-confirm-email"
                  />
                </div>
                {hasPassword && (
                  <div className="space-y-1.5">
                    <Label htmlFor="delete_password">Password</Label>
                    <Input
                      id="delete_password"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      data-testid="delete-account-confirm-password"
                    />
                  </div>
                )}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-account-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="bg-destructive hover:bg-destructive/90"
                  data-testid="delete-account-confirm-btn"
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Permanently delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
